import { createContext, FC, useCallback, useEffect, useState } from 'react';
import {
  GLTFResult,
  useAnimatedMeshViewer,
} from '../hooks/useAnimatedMeshViewer';
import {
  FDAItem,
  StandingDynamicAssessment,
  WalkingDynamicAssessment,
} from '../types';
import { useGLTF } from '@react-three/drei';
import { CircularProgress, Typography } from '@mui/material';

type UseAnimatedMeshViewerReturnType = ReturnType<typeof useAnimatedMeshViewer>;

interface FunctionalDynamicAssessmentProviderReturn {
  balanceData: StandingDynamicAssessment;
  gaitData: WalkingDynamicAssessment;
  balanceControls: UseAnimatedMeshViewerReturnType;
  gaitControls: UseAnimatedMeshViewerReturnType;
  handleSwitch: (view: 'balance' | 'gait') => void;
  selectedView: 'balance' | 'gait';
  cameraFollow: boolean;
  handleCameraFollow: (follow: boolean) => void;
}

const gltfCache: { [url: string]: Promise<string> } = {};
const defaultGLTFUrl =
  'https://momentumhealth-public.s3.ca-central-1.amazonaws.com/stylized_low_poly_tree_-_game-ready.glb';

export const FunctionalDynamicAssessmentContext =
  createContext<FunctionalDynamicAssessmentProviderReturn>(
    {} as FunctionalDynamicAssessmentProviderReturn
  );

interface FunctionalDynamicAssessmentProviderProps {
  fdaData: FDAItem;
  authToken: string;
  // fdaData: {
  //   balance_glb_url: string;
  //   gait_glb_url: string;
  //   balance_data: BalanceData;
  //   gait_data: GaitData;
  // };
  children: React.ReactNode;
}

export const FunctionalDynamicAssessmentProvider: FC<
  FunctionalDynamicAssessmentProviderProps
> = ({ children, fdaData, authToken }) => {
  const {
    blobUrl: standingArrayBuffer,
    isLoading: isStandingLoading,
    progress: standingProgress,
  } = useGetGLTFArrayBuffer(fdaData.standing.output_stream, authToken);

  const {
    blobUrl: walkingArrayBuffer,
    isLoading: isWalkingLoading,
    progress: walkingProgress,
  } = useGetGLTFArrayBuffer(fdaData.walking.output_stream, authToken);

  const hasStandingUrl = Boolean(fdaData.standing.output_stream);
  const hasWalkingUrl = Boolean(fdaData.walking.output_stream);

  const shouldShowLoading =
    (hasStandingUrl && isStandingLoading) ||
    (hasWalkingUrl && isWalkingLoading);

  if (shouldShowLoading) {
    const progress = Math.max(standingProgress, walkingProgress);
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '85vh',
          gap: 16,
        }}
      >
        <Typography variant='h6'>Loading Functional Assessment Data</Typography>
        <CircularProgress
          variant={progress === 100 ? 'indeterminate' : 'determinate'}
          value={progress}
          size={60}
        />
        <Typography variant='body2' color='textSecondary'>
          {Math.max(standingProgress, walkingProgress)}% Complete
        </Typography>
      </div>
    );
  }

  return (
    <ContextProvider
      fdaData={fdaData}
      standing={standingArrayBuffer}
      walking={walkingArrayBuffer}
      children={children}
    />
  );
};

const ContextProvider = ({
  children,
  fdaData,
  standing,
  walking,
}: {
  children: React.ReactNode;
  fdaData: FDAItem;
  standing: string | null; // blobUrl
  walking: string | null; // blobUrl
}) => {
  const standingGLTF = useGLTF(standing || defaultGLTFUrl);
  const walkingGLTF = useGLTF(walking || defaultGLTFUrl);

  // as GLTFResult;
  const balanceControls = useAnimatedMeshViewer({
    gltf: standingGLTF as GLTFResult,
  });
  const gaitControls = useAnimatedMeshViewer({
    gltf: walkingGLTF as GLTFResult,
  });
  const [selectedView, setSelectedView] = useState<'balance' | 'gait'>(
    'balance'
  );
  const [cameraFollow, setCameraFollow] = useState(false);

  const handleSwitch = (view: 'balance' | 'gait') => {
    setSelectedView(view);
    balanceControls.stop();
    gaitControls.stop();
  };

  const handleCameraFollow = (follow: boolean) => {
    setCameraFollow(follow);
  };

  const context = {
    balanceData: fdaData.standing,
    gaitData: fdaData.walking,
    balanceControls,
    gaitControls,
    selectedView,
    handleSwitch,
    cameraFollow,
    handleCameraFollow,
  };

  return (
    <FunctionalDynamicAssessmentContext.Provider value={context}>
      {children}
    </FunctionalDynamicAssessmentContext.Provider>
  );
};

const useGetGLTFArrayBuffer = (url: string | null, authToken: string) => {
  const [isLoading, setIsLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [progress, setProgress] = useState(0); // Add progress state
  const [error, setError] = useState<string | null>(null);

  // Also cleanup when the hook dependencies change (url or authToken)
  useEffect(() => {
    // Cleanup previous blob URL when starting a new request
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [url, authToken, blobUrl]);

  const fetchGLTFData = async (
    url: string,
    authToken: string
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.open('GET', url, true);
      xhr.setRequestHeader('Authorization', 'Bearer ' + authToken);
      xhr.responseType = 'arraybuffer';

      xhr.onprogress = (event) => {
        const fileSize = xhr.getResponseHeader('File-Size');
        if (fileSize) {
          setProgress((prev) => {
            const total = parseInt(fileSize, 10);
            const percentComplete = (event.loaded / total) * 100;
            return Math.max(prev, Math.round(percentComplete));
          });
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          try {
            const arrayBuffer = xhr.response;
            const blob = new Blob([arrayBuffer]);
            const blobUrl = URL.createObjectURL(blob);
            resolve(blobUrl);
          } catch (error) {
            reject(error);
          }
        } else {
          reject(new Error(`HTTP Error: ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error('Network error'));
      xhr.ontimeout = () => reject(new Error('Timeout'));
      xhr.send();
    });
  };

  const getArrayBuffer = useCallback(async () => {
    if (!url || !authToken) {
      return;
    }

    setIsLoading(true);
    setProgress(0);
    setError(null);

    try {
      // Check if already in cache
      if (url in gltfCache) {
        const cachedBlobUrl = await gltfCache[url];
        setBlobUrl(cachedBlobUrl);
        setProgress(100);
        setIsLoading(false);
        return;
      }

      // If not in cache, fetch and cache it
      gltfCache[url] = fetchGLTFData(url, authToken);
      const blobUrl = await gltfCache[url];
      setBlobUrl(blobUrl);
      setProgress(100);
    } catch (error) {
      console.error('Error loading GLTF:', error);
      setError('Failed to load file');
      // Remove failed request from cache
      delete gltfCache[url];
    } finally {
      setIsLoading(false);
    }
  }, [url, authToken]);

  useEffect(() => {
    getArrayBuffer();
  }, [getArrayBuffer]);

  return {
    isLoading,
    blobUrl,
    progress, // Export progress
    error,
  };
};
